import { signOut, fetchAuthSession, getCurrentUser } from "../../util/amplifyAuth";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import './UserHeader.css';
import account from "../../assets/profile.png";
import { clearFileCache } from "../../util/FileCacheUtil";
import { clearComplianceData } from "../../compliance/complianceUtils";
import { getCachedProfile } from "../../util/CacheUtil";

import { TABLET } from '../../util/Constants';

const IDLE_TIMEOUT = 3000; // Hide after 3 seconds of no scrolling

function UserHeader({ children, onLogoClick, tabControl, doctorMode, hideProfile, minimal, exportButton, sharingMode, sharingLabel, onBack, demoMode }) {
  // In demo mode there is no real auth/profile: hide the profile dropdown, badge the
  // header, and surface a Home button back to the real app.
  const effectiveHideProfile = hideProfile || demoMode;
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  // Demo: resolve auth WITHOUT redirecting, so the header button can be "Home"
  // (logged in) vs "Sign up now" (anonymous). null = not yet resolved.
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const accountRef = useRef(null);
  const lastScrollY = useRef(0);
  const idleTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const profile = getCachedProfile();
    if (profile?.givenName || profile?.familyName) {
      setUserName([profile.givenName, profile.familyName].filter(Boolean).join(' '));
    }
    fetchAuthSession().then(session => {
      const payload = session.tokens?.idToken?.payload;
      if (payload) {
        setUserEmail(payload.email || '');
        // Fall back to Cognito claims if cached profile had no name
        if (!profile?.givenName && !profile?.familyName) {
          const given = payload.given_name || '';
          const family = payload.family_name || '';
          setUserName([given, family].filter(Boolean).join(' '));
        }
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!demoMode) return;
    getCurrentUser().then(() => setIsLoggedIn(true)).catch(() => setIsLoggedIn(false));
  }, [demoMode]);

  const handleSignOut = async () => {
    try {
      const { userId } = await getCurrentUser();
      clearComplianceData(userId);
    } catch {}
    clearFileCache();
    await signOut();
    window.location.reload();
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    }
  }, []);

  // Smart header scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY.current;
      const scrollingUp = currentScrollY < lastScrollY.current;
      const atTop = currentScrollY < 10;

      // Clear any existing idle timer
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }

      if (atTop) {
        // Always show at top of page
        setIsHidden(false);
      } else if (scrollingDown && currentScrollY > 60) {
        // Hide when scrolling down (past header height)
        setIsHidden(true);
      } else if (scrollingUp) {
        // Show when scrolling up
        setIsHidden(false);

        // Only auto-hide on idle at tablet/mobile sizes
        if (window.innerWidth <= TABLET) {
          idleTimer.current = setTimeout(() => {
            if (window.scrollY > 60) {
              setIsHidden(true);
            }
          }, IDLE_TIMEOUT);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`contentHeader ${isHidden ? 'header-hidden' : ''} ${sharingMode ? 'sharing-mode' : ''}`}>
      {onBack && (
        <button className="header-back-btn" onClick={onBack} title="Back to list">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}
      <h3 className="brandName" onClick={onLogoClick || scrollToTop}>PrimeMaxing</h3>
      {demoMode && <span className="demo-badge">Demo</span>}
      {sharingLabel && <span className="header-sharing-label">{sharingLabel}</span>}
      {tabControl && <div className="headerTabControl">{tabControl}</div>}
      <div className="headerActions">
        {children}
        {demoMode && isLoggedIn !== null && (
          <button
            className="header-home-button"
            onClick={() => navigate(isLoggedIn ? "/home" : "/signup")}
          >
            {isLoggedIn ? "Home" : "Sign up now"}
          </button>
        )}
      </div>
      {!effectiveHideProfile && (
        <div className="accountContainer" ref={accountRef}>
          <img
            src={account}
            onClick={() => setIsOpen(!isOpen)}
            alt="Account"
          />
          {isOpen && (
              <div className="profileDropdown">
                  <div className="dropdown-export-wrapper" onClick={() => setIsOpen(false)}>
                      {exportButton}
                  </div>
                  <a onClick={() => navigate("/settings")}>
                      Settings
                  </a>
                  <div className="dropdown-divider" />
                  <a onClick={handleSignOut}>
                      Sign Out
                  </a>
                  {(userName || userEmail) && (
                      <>
                          <div className="dropdown-divider" />
                          <div className="dropdown-user-info">
                              {userName && <span className="dropdown-user-name">{userName}</span>}
                              {userEmail && <span className="dropdown-user-email">{userEmail}</span>}
                          </div>
                      </>
                  )}
              </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UserHeader;
